// A short local road is normally preferable for an auto ride, but never at
// the expense of a materially slower trip. Google returns its default route
// first; when it has alternatives, choose the shortest route that is still
// close to the fastest ETA.
export const MAX_ROUTE_DURATION_TRADEOFF_SECONDS = 3 * 60;

export const choosePreferredDrivingRoute = (routes) => {
  if (!Array.isArray(routes) || routes.length === 0) return null;

  const candidates = routes.map((route) => {
    const leg = route?.legs?.[0];
    return {
      route,
      distanceM: Number(leg?.distance?.value),
      durationS: Number(leg?.duration?.value),
    };
  }).filter((candidate) => (
    Number.isFinite(candidate.distanceM)
    && candidate.distanceM >= 0
    && Number.isFinite(candidate.durationS)
    && candidate.durationS >= 0
  ));

  // A malformed alternative must never make a normal route fail. Preserve the
  // old first-route behaviour if Google omits usable distance/duration data.
  if (candidates.length === 0) return routes[0];

  const fastestDurationS = Math.min(...candidates.map((candidate) => candidate.durationS));
  const comparable = candidates.filter((candidate) => (
    candidate.durationS <= fastestDurationS + MAX_ROUTE_DURATION_TRADEOFF_SECONDS
  ));

  return comparable.reduce((best, candidate) => {
    if (candidate.distanceM < best.distanceM) return candidate;
    if (candidate.distanceM === best.distanceM && candidate.durationS < best.durationS) return candidate;
    return best;
  }, comparable[0]).route;
};

// The newer Routes library returns `distanceMeters`, `durationMillis` and a
// `path` of points. Everything downstream (the route chooser, the fare, the
// map line) reads the older Directions shape, so reshape one into the other
// here instead of teaching each caller two formats. Returns null for a route
// without a usable distance, so the caller can fall back to the car route.
export const toDirectionsRoute = (route, makeLatLng) => {
  const distanceM = Number(route?.distanceMeters);
  const durationS = Number(route?.durationMillis) / 1000;
  if (!Number.isFinite(distanceM) || distanceM <= 0) return null;
  if (!Number.isFinite(durationS) || durationS < 0) return null;
  const path = (Array.isArray(route.path) ? route.path : [])
    .map((p) => {
      const lat = typeof p?.lat === 'function' ? p.lat() : p?.lat;
      const lng = typeof p?.lng === 'function' ? p.lng() : p?.lng;
      return Number.isFinite(lat) && Number.isFinite(lng) ? makeLatLng(lat, lng) : null;
    })
    .filter(Boolean);
  if (path.length < 2) return null;
  const minutes = Math.max(1, Math.round(durationS / 60));
  return {
    overview_path: path,
    legs: [{
      distance: { value: distanceM, text: `${(distanceM / 1000).toFixed(1)} km` },
      duration: { value: durationS, text: `${minutes} min` },
    }],
  };
};

// A two-wheeler route is only trusted when it saves a lot over the car route:
// that is a real shortcut closed to cars (Piler's Sadum Rd: 2.9 km against a
// 6.1 km car detour). A small saving is more likely a lane an auto cannot use,
// so the car route wins. Tested on 109 real completed rides (2026-10-24).
export const TWO_WHEELER_MIN_SAVING_KM = 1.5;

const routeMetres = (route) => Number(route?.legs?.[0]?.distance?.value);

export const chooseRouteByVehicle = (carRoutes, bikeRoutes) => {
  const car = choosePreferredDrivingRoute(carRoutes);
  const bike = choosePreferredDrivingRoute(bikeRoutes);
  if (!car) return bike;
  if (!bike) return car;
  const carM = routeMetres(car);
  const bikeM = routeMetres(bike);
  if (!Number.isFinite(carM) || !Number.isFinite(bikeM)) return car;
  return carM - bikeM > TWO_WHEELER_MIN_SAVING_KM * 1000 ? bike : car;
};
